import React, {useState} from 'react';
import {Navigate, useLocation} from "react-router-dom";
import {Box, Container, Divider, Tab, Tabs, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import NavigationBarView from "../../navbar/components/NavigationBarView";
import useReviewSummary from "../hooks/useReviewSummary";
import ColoredWordListView from "../../vocabulary/components/WordListView/components/ColoredWordListView";
import {LoadingPageView} from "../../../common/components/LoadingPageView";


function ReviewSummary() {

    let location = useLocation();
    const theme = useTheme();

    const {correctReviews, failedReviews, isPending, isSuccess, isError} = useReviewSummary(location.state?.sessionId);

    const [tabIndex, setTabIndex] = useState(0);

    if (!location.state?.sessionId || isError) {
        return <Navigate to="/" replace />;
    }

    if (isPending) {
        return <LoadingPageView message={"Loading review summary..."} />
    }

    if (isSuccess && correctReviews.length === 0 && failedReviews.length === 0) {
        return <Navigate to="/" replace />;
    }

    return (
        <Container disableGutters={true} maxWidth={false}>
            <NavigationBarView displayDeckSelectionDropdown={false} displaySearchBar={false} displayVocabularyLink={false}/>
            <Typography variant="h4" align="center" sx={{my: 3, fontWeight: "bold"}}>Review Summary</Typography>
            <Box sx={{maxWidth: 1300, margin: '0 auto', border: 1, borderRadius: 2, borderColor: 'divider', backgroundColor: theme.palette.grey[200]}}>
                <Tabs value={tabIndex}
                      onChange={(e, newValue) => setTabIndex(newValue)}
                      textColor="inherit"
                      indicatorColor="primary"
                      variant="fullWidth"
                      sx={{'& .MuiTabs-indicator': {height: 3, backgroundColor: 'secondary.main'}}}
                >
                    <Tab value={0} label="Vocabulary" sx={{textTransform: 'none', fontFamily: 'inherit', fontSize: 'h6.fontSize', fontWeight: 'bold', borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider'}}/>
                    <Tab value={1} label="Exercises" sx={{textTransform: 'none', fontFamily: 'inherit', fontSize: 'h6.fontSize', fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider'}}/>
                </Tabs>
                {tabIndex === 0 && <VocabularySummaryTabView/>}
                {tabIndex === 1 && <ExerciseSummaryTabView/>}
            </Box>
        </Container>
    )

    function VocabularySummaryTabView() {

        function ReviewSection({title, headerColor, children}: {title: string, headerColor: string, children: React.ReactNode}) {
            return (
                <Box sx={{border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mt: 2}}>
                    <Box sx={{backgroundColor: headerColor, px: 2, py: 1}}>
                        <Typography variant="h6" >{title}</Typography>
                    </Box>
                    <Divider/>
                    <Box sx={{backgroundColor: 'background.default', px: 2, pb: 2, pt: 1}}>
                        {children}
                    </Box>
                </Box>
            );
        }

        return (
            <Box sx={{px: 2, pb: 2}}>
                {failedReviews.length > 0 && (
                    <ReviewSection title={`${failedReviews.length} Wrong Reviews`} headerColor="error.main">
                        <ColoredWordListView
                            words={failedReviews.map(({word, newStage}) => ({...word, stage: newStage}))}
                            colorByStages={true}
                            stageSymbol="down"
                        />
                    </ReviewSection>
                )}
                {correctReviews.length > 0 && (
                    <ReviewSection title={`${correctReviews.length} Correct Reviews`} headerColor="success.main">
                        <ColoredWordListView
                            words={correctReviews.map(({word, newStage}) => ({...word, stage: newStage}))}
                            colorByStages={true}
                            stageSymbol="up"
                        />
                    </ReviewSection>
                )}
            </Box>
        )
    }

    function ExerciseSummaryTabView() {
        return (
            <Box sx={{width: '100%', px: 2, height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Typography variant={"body1"}>Coming soon</Typography>
            </Box>
        )
    }


}

export default ReviewSummary;
